import { useState, type ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";
import { MAX_PROJECT_NAME_LENGTH } from "../model/storage.ts";
import { DownloadIcon } from "./DownloadIcon.tsx";
import { DuplicateIcon } from "./DuplicateIcon.tsx";
import { MenuIcon } from "./MenuIcon.tsx";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu.tsx";
import { RenameIcon } from "./RenameIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { TextInputDialog } from "./TextInputDialog.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

type ProjectDialog = "rename" | "duplicate" | "delete" | null;

/**
 * Both the expanded icon-row and collapsed "⋮" popover variants are always
 * in the DOM at once — a CSS media query (workspace.css) picks which one
 * shows, not JS, so there's no flash of the wrong variant on first paint.
 */
export function ProjectMenu({
  projectName,
  open,
  onToggle,
  onClose,
  onRename,
  onDuplicate,
  onExport,
  onDelete,
}: {
  projectName: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onRename: (name: string) => void;
  onDuplicate: (name: string) => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const [dialog, setDialog] = useState<ProjectDialog>(null);

  const actions: {
    key: string;
    label: string;
    icon: ReactNode;
    danger?: boolean;
    onClick: () => void;
  }[] = [
    {
      key: "rename",
      label: t("projectMenu.rename"),
      icon: <RenameIcon />,
      onClick: () => setDialog("rename"),
    },
    {
      key: "duplicate",
      label: t("projectMenu.duplicate"),
      icon: <DuplicateIcon />,
      onClick: () => setDialog("duplicate"),
    },
    {
      key: "export",
      label: t("toolbar.exportInp"),
      icon: <DownloadIcon />,
      onClick: onExport,
    },
    {
      key: "delete",
      label: t("projectMenu.delete"),
      icon: <TrashIcon />,
      danger: true,
      onClick: () => setDialog("delete"),
    },
  ];

  return (
    <>
      <div className="project-menu-expanded">
        {actions.map((action) => (
          <button
            key={action.key}
            className={
              action.danger
                ? "button-icon topbar-icon-button button-icon-danger"
                : "button-icon topbar-icon-button"
            }
            onClick={action.onClick}
            aria-label={action.label}
            title={action.label}
          >
            {action.icon}
          </button>
        ))}
      </div>
      <div className="project-menu-collapsed">
        <DropdownMenu
          open={open}
          onToggle={onToggle}
          onClose={onClose}
          triggerClassName="button-icon topbar-icon-button dropdown-trigger"
          triggerLabel={t("projectMenu.title")}
          triggerIcon={<MenuIcon />}
          align="left"
        >
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.key}
              icon={action.icon}
              label={action.label}
              danger={action.danger}
              onClick={() => {
                onClose();
                action.onClick();
              }}
            />
          ))}
        </DropdownMenu>
      </div>
      {dialog === "rename" && (
        <TextInputDialog
          title={t("projectMenu.renameTitle")}
          initialValue={projectName}
          maxLength={MAX_PROJECT_NAME_LENGTH}
          onSave={(name) => {
            onRename(name);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "duplicate" && (
        <TextInputDialog
          title={t("projectMenu.duplicateTitle")}
          initialValue={t("projectMenu.copyNameSuffix", { name: projectName })}
          maxLength={MAX_PROJECT_NAME_LENGTH}
          onSave={(name) => {
            onDuplicate(name);
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "delete" && (
        <ConfirmDialog
          title={t("projectMenu.deleteTitle")}
          message={t("projectMenu.deleteMessage", { name: projectName })}
          confirmLabel={t("projectMenu.deleteConfirm")}
          onConfirm={onDelete}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}
